import React, { cloneElement, isValidElement, type ButtonHTMLAttributes, type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { normalizeStatusTone, type StatusTone } from "./tokens.js";

export function PageShell({ children, className = "", ...props }: HTMLAttributes<HTMLElement>): JSX.Element {
  return <section {...props} className={`simo-ds-page simo-thin-scroll ${className}`.trim()} data-design-system-page><div className="simo-ds-page__inner">{children}</div></section>;
}

export function Panel({ children, className = "", as: Tag = "section", ...props }: HTMLAttributes<HTMLElement> & { as?: "section" | "article" | "aside" | "header" }): JSX.Element {
  return <Tag {...props} className={`simo-ds-panel ${className}`.trim()}>{children}</Tag>;
}

export function Heading({ level = 2, children, className = "" }: { level?: 1 | 2 | 3; children: ReactNode; className?: string }): JSX.Element {
  const Tag = `h${level}` as const;
  return <Tag className={`simo-ds-heading ${className}`.trim()}>{children}</Tag>;
}

export function Kicker({ children, className = "" }: { children: ReactNode; className?: string }): JSX.Element {
  return <p className={`simo-ds-kicker ${className}`.trim()}>{children}</p>;
}

export function Metric({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return <div className="simo-ds-panel min-w-0 px-3 py-2.5"><div className="text-lg font-semibold text-jarvis-text">{value}</div><div className="mt-1 text-xs font-semibold uppercase tracking-[0.1em] text-jarvis-muted">{label}</div></div>;
}

export type ActionVariant = "neutral" | "primary" | "success" | "warning" | "danger";
export function ActionButton({ variant = "neutral", className = "", type = "button", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ActionVariant }): JSX.Element {
  return <button {...props} type={type} data-variant={variant} className={`simo-ds-button ${className}`.trim()} />;
}

export function StatusBadge({ status, label, tone }: { status: string; label?: string; tone?: StatusTone }): JSX.Element {
  const resolvedTone = tone ?? normalizeStatusTone(status);
  return <span className="simo-ds-status" data-status-tone={resolvedTone}><span>{label ?? status}</span></span>;
}

export type StateKind = "info" | "success" | "warning" | "error";
export function StateMessage({ kind = "info", label, children, busy = false }: { kind?: StateKind; label: string; children?: ReactNode; busy?: boolean }): JSX.Element {
  const role = kind === "error" ? "alert" : "status";
  return <div className="simo-ds-state" data-kind={kind} role={role} aria-live={kind === "error" ? "assertive" : "polite"} aria-busy={busy || undefined}><strong>{label}</strong>{children && <div className="mt-1">{children}</div>}</div>;
}

export function FormField({ id, label, help, error, required = false, children }: { id: string; label: string; help?: string; error?: string | null; required?: boolean; children: ReactElement }): JSX.Element {
  const helpId = help ? `${id}-help` : null;
  const errorId = error ? `${id}-error` : null;
  const existingDescribedBy = isValidElement(children) && typeof (children.props as Record<string, unknown>)["aria-describedby"] === "string" ? String((children.props as Record<string, unknown>)["aria-describedby"]) : null;
  const describedBy = [existingDescribedBy, helpId, errorId].filter(Boolean).join(" ") || undefined;
  const control = isValidElement(children) ? cloneElement(children as ReactElement<Record<string, unknown>>, {
    id,
    "aria-describedby": describedBy,
    "aria-invalid": error ? "true" : undefined,
    "aria-required": required ? "true" : undefined,
    required: required || undefined,
  }) : children;
  return <div className="simo-ds-field"><label htmlFor={id}>{label}{required ? " *" : ""}</label>{control}{help && <p id={helpId!} className="simo-ds-field__help">{help}</p>}{error && <p id={errorId!} className="simo-ds-field__error" role="alert">{error}</p>}</div>;
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }): JSX.Element {
  return <StateMessage kind="info" label={title}>{children}</StateMessage>;
}
